import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Camera, FileText, Upload } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { ReferralDocument, ReferralPage, providerDocumentsApi } from "./providerDocumentsApi";

/**
 * Audiology referrals (provider Cindy Aragon, 2026-10-21): the signed referral a provider hands the nurse or DON
 * (Debrox drops, an ENT referral), filed per resident "just in case they say we never gave it to the nurse or DON".
 *
 * A PDF or a photo of the signed paper. On a phone, "Take a photo" opens the camera. The server decides who may
 * upload (the providers who work the resident) and who may read (those providers and the office); `canUpload` only
 * decides whether the upload controls are offered, and the server's own `can_upload` must agree. Files are never
 * deleted here; a file opens through a five-minute link, and every open is recorded.
 */
export interface ProviderDocumentsProps {
  facilityId: string;
  patientId: string;
  canUpload: boolean;
}

const FALLBACK_ACCEPT = ["application/pdf", "image/jpeg", "image/png", "image/heic", "image/webp"];
const FALLBACK_MAX = 20 * 1024 * 1024;

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "time not recorded";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function sizeWords(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "size not recorded";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const kindWords = (contentType: string) => (contentType === "application/pdf" ? "PDF" : "Photo");

/** What the screen can say before sending: the server says the rest (and sniffs the bytes). */
export function fileProblem(file: File, accept: string[], maxBytes: number): string | null {
  const type = (file.type || "").toLowerCase();
  if (!accept.includes(type)) return "A referral is a PDF or a photo (JPEG, PNG, HEIC or WebP). Choose another file.";
  if (file.size === 0) return "That file is empty. Choose another file.";
  if (file.size > maxBytes) return `That file is ${sizeWords(file.size)}; a referral is at most ${sizeWords(maxBytes)}. Take the photo again at a lower size, or scan it as a PDF.`;
  return null;
}

type Load =
  | { state: "loading" }
  | { state: "ready"; page: ReferralPage }
  | { state: "refused"; message: string }
  | { state: "error"; message: string };

function refusalWords(status: number | undefined, code: string | undefined, message: string | undefined): Load {
  if (status === undefined && !code) return { state: "error", message: "Could not reach eMerge to load the referrals." };
  if (status === 403) return { state: "refused", message: "Referrals are for the providers who work this resident and the office." };
  if (status === 404) return { state: "refused", message: message || "This resident is not confirmed at this building." };
  return { state: "error", message: message || "The referrals could not be loaded." };
}

function DocumentItem({
  doc, onOpen, opening, link,
}: { doc: ReferralDocument; onOpen: (d: ReferralDocument) => void; opening: boolean; link: string | null }) {
  const who = doc.uploaded_by.is_you ? "You" : doc.uploaded_by.name || "A provider";
  const name = doc.original_filename || `Audiology referral (${kindWords(doc.content_type)})`;
  return (
    <li className="fw-private-note">
      <p>
        <FileText size={14} aria-hidden="true" /> <span style={{ overflowWrap: "anywhere" }}>{name}</span>
        {doc.misfiled ? " · marked not this resident's" : ""}
      </p>
      <p className="fw-muted">
        {kindWords(doc.content_type)} · {sizeWords(doc.byte_size)} · filed by <strong>{who}</strong> ·{" "}
        <time dateTime={doc.created_at}>{when(doc.created_at)}</time>
      </p>
      <div className="fw-actions">
        <Button variant="outline" type="button" onClick={() => onOpen(doc)} disabled={opening} aria-label={`Open ${name}`}>
          {opening ? "Opening…" : "Open"}
        </Button>
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer">
            Open {name} (link works for 5 minutes)
          </a>
        )}
      </div>
    </li>
  );
}

export function ProviderDocuments({ facilityId, patientId, canUpload }: ProviderDocumentsProps) {
  const ids = useId();
  const headingId = `${ids}-heading`;
  const fileId = `${ids}-file`;
  const cameraId = `${ids}-camera`;
  const helpId = `${ids}-help`;
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploaded, setUploaded] = useState("");
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState("");
  const [links, setLinks] = useState<Record<string, string>>({});

  const fetchDocs = useCallback(async () => {
    setLoad({ state: "loading" });
    try {
      const r = await providerDocumentsApi.list(facilityId, patientId);
      if (r.success && r.data && Array.isArray(r.data.documents)) setLoad({ state: "ready", page: r.data });
      else if (r.success) setLoad({ state: "error", message: "The referrals could not be loaded." });
      else setLoad(refusalWords(r.status, r.code, r.message));
    } catch {
      setLoad({ state: "error", message: "Could not reach eMerge to load the referrals." });
    }
  }, [facilityId, patientId]);

  useEffect(() => {
    setUploadError("");
    setUploaded("");
    setOpenError("");
    setLinks({});
    fetchDocs();
  }, [fetchDocs]);

  const page = load.state === "ready" ? load.page : null;
  const accept = page?.accept?.length ? page.accept : FALLBACK_ACCEPT;
  const maxBytes = page?.max_bytes || FALLBACK_MAX;
  const uploadable = canUpload && page !== null && page.can_upload && page.installed !== false;

  const send = async (file: File | undefined) => {
    if (!file || !page || uploading) return;
    setUploadError("");
    setUploaded("");
    const problem = fileProblem(file, accept, maxBytes);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploading(true);
    try {
      const r = await providerDocumentsApi.upload(facilityId, patientId, file);
      if (r.success && r.data) {
        const added = r.data.document;
        if (added && added.id) {
          setLoad({ state: "ready", page: { ...page, documents: [added, ...page.documents.filter((d) => d.id !== added.id)] } });
        } else {
          await fetchDocs();
        }
        setUploaded(r.data.deduplicated ? "This exact file was already filed on this resident; nothing new was added." : "Referral filed.");
      } else if (r.status === undefined && !r.code) {
        setUploadError("eMerge did not confirm the upload. Check the list, then try again; the same file is never filed twice.");
      } else {
        setUploadError(r.message || r.error || "The referral was not filed.");
      }
    } catch {
      setUploadError("eMerge did not confirm the upload. Check the list, then try again; the same file is never filed twice.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
      if (cameraInput.current) cameraInput.current.value = "";
    }
  };

  const open = async (doc: ReferralDocument) => {
    setOpenError("");
    setOpening(doc.id);
    // Opened before the request, so a phone's pop-up blocker treats it as the tap's own window.
    const win = typeof window !== "undefined" && typeof window.open === "function" ? window.open("", "_blank") : null;
    try {
      const r = await providerDocumentsApi.link(facilityId, patientId, doc.id);
      if (r.success && r.data && r.data.url) {
        setLinks((m) => ({ ...m, [doc.id]: r.data!.url }));
        if (win) {
          win.opener = null;
          win.location.replace(r.data.url);
        }
      } else {
        if (win) win.close();
        setOpenError(
          r.status === undefined && !r.code
            ? "Could not reach eMerge to open the referral."
            : r.message || r.error || "The referral could not be opened.",
        );
      }
    } catch {
      if (win) win.close();
      setOpenError("Could not reach eMerge to open the referral.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <section className="fw-private-notes" aria-labelledby={headingId} data-testid="provider-documents">
      <h3 id={headingId} className="fw-replacement-title">
        <FileText size={14} aria-hidden="true" /> Documents · audiology referrals
      </h3>
      <p className="fw-muted">
        The signed referral you hand the nurse or DON (Debrox drops, ENT), kept as proof it was given. Only the providers
        who work this resident and the office see these. The facility never does.
      </p>

      {load.state === "loading" && <p role="status" className="fw-muted">Loading referrals…</p>}
      {load.state === "refused" && <p className="fw-muted">{load.message}</p>}
      {load.state === "error" && (
        <div role="status" className="fw-error">
          <p>{load.message}</p>
          <Button variant="outline" type="button" onClick={fetchDocs}>Try again</Button>
        </div>
      )}

      {page && (
        <>
          {page.access === "AUTHOR_ONLY" && (
            <p className="fw-muted">You see only the referrals you filed: you no longer work this resident.</p>
          )}
          {page.documents.length === 0 ? (
            <p className="fw-muted">No referrals filed yet.</p>
          ) : (
            <ul className="fw-private-note-list" aria-label="Referrals, newest first">
              {page.documents.map((d) => (
                <DocumentItem key={d.id} doc={d} onOpen={open} opening={opening === d.id} link={links[d.id] || null} />
              ))}
            </ul>
          )}
          {page.truncated && <p className="fw-muted">Showing the newest {page.limit} referrals.</p>}
          {openError && <p role="alert" className="fw-error">{openError}</p>}

          {canUpload && page.can_upload && page.installed === false && (
            <p className="fw-muted">Referral uploads are not switched on yet.</p>
          )}
          {uploadable && (
            <div role="group" aria-label="File a referral">
              <p id={helpId} className="fw-muted">
                A PDF or a photo of the signed referral, up to {sizeWords(maxBytes)}. Filed referrals cannot be removed here.
              </p>
              {/* Not a <form>: the panel sits inside the workbench's manifest form, and forms do not nest. */}
              <input
                ref={fileInput}
                id={fileId}
                aria-label="Referral file (PDF or photo)"
                tabIndex={-1}
                type="file"
                accept={accept.join(",")}
                aria-describedby={helpId}
                disabled={uploading}
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden" }}
                onChange={(e) => send(e.target.files?.[0])}
              />
              <input
                ref={cameraInput}
                id={cameraId}
                aria-label="Referral photo from the camera"
                tabIndex={-1}
                type="file"
                accept="image/*"
                capture="environment"
                aria-describedby={helpId}
                disabled={uploading}
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden" }}
                onChange={(e) => send(e.target.files?.[0])}
              />
              <div className="fw-actions">
                <Button variant="outline" type="button" disabled={uploading} onClick={() => fileInput.current?.click()}>
                  <Upload size={14} aria-hidden="true" /> Choose a file
                </Button>
                <Button variant="outline" type="button" disabled={uploading} onClick={() => cameraInput.current?.click()}>
                  <Camera size={14} aria-hidden="true" /> Take a photo
                </Button>
              </div>
              {uploadError && <p role="alert" className="fw-error">{uploadError}</p>}
              <p role="status" className="fw-save-status">{uploading ? "Filing the referral…" : uploaded}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
export default ProviderDocuments;
